export class PacketManager {
  // Packet Header Structure (28 bytes total):
  // [CheckCode: 4 bytes] -> 0x88 (Little Endian)
  // [MsgID: 4 bytes] -> Command ID (Little Endian)
  // [TotalSize: 4 bytes] -> Total length of the packet including header (Little Endian)
  // [PacketNo/Seq: 4 bytes] -> Sequence number (Little Endian)
  // [TargetID: 8 bytes] -> Target ID (Little Endian, split into 2 * 4 bytes)
  // [UserData: 4 bytes] -> User data (Little Endian)
  // [Payload: N bytes] -> Protobuf serialized data

  private static readonly HEADER_SIZE = 28 // 28 bytes header
  private static readonly CHECK_CODE = 0x88

  /**
   * Encodes a protobuf payload with a binary header.
   * @param seq Sequence number for the packet
   * @param msgId Command ID for the packet
   * @param pbPayload Serialized protobuf Uint8Array
   * @returns ArrayBuffer ready to be sent over WebSocket
   */
  public static encode(seq: number, msgId: number, pbPayload: Uint8Array): ArrayBuffer {
    const totalLength = this.HEADER_SIZE + pbPayload.length
    const buffer = new ArrayBuffer(totalLength)
    const dataView = new DataView(buffer)

    // Write CheckCode (0x88)
    dataView.setUint32(0, this.CHECK_CODE, true)
    
    // Write MsgID
    dataView.setUint32(4, msgId, true)
    
    // Write Size
    dataView.setUint32(8, totalLength, true)
    
    // Write Seq / PacketNo
    dataView.setUint32(12, seq, true)
    
    // Write TargetID (64-bit as 2x 32-bit zeroes for now)
    dataView.setUint32(16, 0, true)
    dataView.setUint32(20, 0, true)
    
    // Write UserData
    dataView.setUint32(24, 0, true)

    // Write Payload
    const payloadView = new Uint8Array(buffer, this.HEADER_SIZE)
    payloadView.set(pbPayload)

    return buffer
  }

  /**
   * Decodes a binary packet from the server.
   * @param arrayBuffer The raw ArrayBuffer received from WebSocket
   * @returns An object containing the msgId and the protobuf payload Uint8Array
   */
  public static decode(arrayBuffer: ArrayBuffer): { msgId: number; payload: Uint8Array } | null {
    if (arrayBuffer.byteLength < this.HEADER_SIZE) {
      console.warn('Packet length is less than header size')
      return null
    }

    const dataView = new DataView(arrayBuffer)

    // Read Header
    const checkCode = dataView.getUint32(0, true)
    
    if (checkCode !== this.CHECK_CODE) {
      console.error('Invalid message header check code')
      return null
    }

    const msgId = dataView.getUint32(4, true)
    const length = dataView.getUint32(8, true)
    // const packetNo = dataView.getUint32(12, true)
    // const targetIdLow = dataView.getUint32(16, true)
    // const targetIdHigh = dataView.getUint32(20, true)
    // const userData = dataView.getUint32(24, true)

    // Validate length
    if (arrayBuffer.byteLength < length) {
      console.warn(
        `Incomplete packet. Expected ${length} bytes, got ${arrayBuffer.byteLength} bytes.`,
      )
      // In a robust implementation, this would buffer the data until the full packet arrives.
      // For WebSocket, frames normally arrive complete, so we just log a warning for now.
    }

    // Extract Payload
    const payload = new Uint8Array(arrayBuffer, this.HEADER_SIZE, length - this.HEADER_SIZE)

    return { msgId, payload }
  }
}
